import { useMemo, type ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { Stack } from '@mui/material';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DeviceInfoSection } from './DeviceInfoSection';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import {
  companyLowPowerSettingsAtom,
  companyROCSettingsAtom,
  companyWeathermodeSettingsAtom,
} from '../../../models/atoms/company_atoms';
import { MHMLabel } from '../MHM/MHMLabel';
import { DeviceWarningLevel } from './DeviceWarningLevel';

function InvertedLabel({ label }: { label: ReactNode }) {
  return (
    <MHMLabel
      label={label}
      color="primary"
      variant="filled"
      sx={{ borderColor: 'primary.main' }}
      labelProps={{ medium: true }}
    />
  );
}

type Props = {
  canEditSession: boolean;
};

export function DeviceReadings({ canEditSession }: Props) {
  const { t, distance, language } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const companyLowPowerSettings = useRecoilValue(companyLowPowerSettingsAtom);
  const companyWeathermodeSettings = useRecoilValue(
    companyWeathermodeSettingsAtom,
  );
  const companyROCSettings = useRecoilValue(companyROCSettingsAtom);

  const pipeDiameterText = useMemo(() => {
    if (!selectedDevice?.pipe_diameter_mm) {
      return 0;
    }

    // ft-in for en-US, mm for other languages
    const diameterDefault = distance.show(selectedDevice.pipe_diameter_mm);
    const diameterCM = distance.show(selectedDevice.pipe_diameter_mm, {
      unit: 'cm',
    });
    if (language !== 'en-US') {
      return `${diameterCM} (${diameterDefault})`;
    }
    const diameterInches = distance.show(selectedDevice.pipe_diameter_mm, {
      unit: 'in',
    });
    return selectedDevice.pipe_diameter_mm < 304 // 1 foot in mm, rounded down to commodate for floating point errors
      ? diameterInches
      : `${diameterDefault} (${diameterInches})`;
  }, [language, selectedDevice, distance]);

  if (!selectedDevice) {
    return null;
  }

  const showLowerPower = companyLowPowerSettings?.low_power_on;
  const isLowPower =
    showLowerPower &&
    selectedDevice.battery_percentage <
      companyLowPowerSettings.low_power_battery_threshold;
  const showWeatherMode = companyWeathermodeSettings?.weathermode_on;
  const showROC = companyROCSettings?.roc_mode_on;
  const showHighLevel = selectedDevice.labels.includes('High Level');
  const fillDistance = distance.show(
    (selectedDevice.backend_max_height_mm *
      selectedDevice.current_level_percentage) /
      100,
  );

  const modes = [
    { label: t('Low Power'), show: showLowerPower, isActive: isLowPower },
    {
      label: t('Weather Mode'),
      show: showWeatherMode,
      isActive: selectedDevice.device_weathermode_on,
    },
    {
      label: t('High ROC'),
      show: showROC,
      isActive: selectedDevice.labels.includes('High ROC'),
    },
    {
      label: t('High Level'),
      show: showHighLevel,
      isActive: selectedDevice.labels.includes('High Level'),
    },
  ];

  return (
    <DeviceInfoSection>
      <DeviceInfoSection.Heading>{t('Readings')}</DeviceInfoSection.Heading>
      <DeviceInfoSection.TextEntry
        label={t('Max Height:')}
        text={distance.show(selectedDevice.backend_max_height_mm)}
      />
      <DeviceInfoSection.TextEntry
        label={t('Fill Percentage:')}
        textProps={{ 'data-testid': 'fill_percentage' }}
        text={`${selectedDevice.current_level_percentage.toFixed(1)}% (${fillDistance})`}
      />
      {!!selectedDevice.bench_height_mm && (
        <DeviceInfoSection.TextEntry
          label={t('Bench Height:')}
          textProps={{ 'data-testid': 'bench_height_mm' }}
          text={`${(100 * (selectedDevice.bench_height_mm / selectedDevice.backend_max_height_mm)).toFixed(1)}% (${distance.show(selectedDevice.bench_height_mm)})`}
        />
      )}
      {!!selectedDevice.pipe_diameter_mm && (
        <DeviceInfoSection.TextEntry
          label={t('Pipe Diameter:')}
          text={pipeDiameterText}
          textProps={{ 'data-testid': 'pipe_diameter' }}
        />
      )}
      <DeviceWarningLevel canEditSession={canEditSession} />
      <DeviceInfoSection.Entry direction="column" label={t('Modes:')}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {modes.map(mode => {
            if (!mode.show) {
              return null;
            }
            if (mode.isActive) {
              return <InvertedLabel key={mode.label} label={mode.label} />;
            }
            return (
              <MHMLabel
                key={mode.label}
                label={mode.label}
                labelProps={{ medium: true }}
              />
            );
          })}
        </Stack>
      </DeviceInfoSection.Entry>
    </DeviceInfoSection>
  );
}
